import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Gamepad2, Users, Landmark, Crown, Globe, Building2, Factory, Wheat, Sparkles, Shield, DollarSign, AlertTriangle, Briefcase, ChevronRight, ArrowLeft } from 'lucide-react';
import useGameStore from '../../game/gameStore';
import useAuthStore from '../../game/authStore';
import CandidatoAvatar from '../../components/CandidatoAvatar';
import SEO from '../../components/SEO';
import { capitalize, formatUSD, formatBRL, formatNum, GAME_YEAR, STAT_LABELS } from './constants';
import AvatarSelector from './AvatarSelector';
import AuthScreen from './AuthScreen';
import GameStateMap from './GameStateMap';
import GameCityMap from './GameCityMap';
import CityIntroScreen from './CityIntroScreen';
import VereadorSelector from './VereadorSelector';
import MeetVereadorScreen from './MeetVereadorScreen';
import GameBoard from './GameBoard';
import RadarChart from './RadarChart';
import '../../css/game-layout.css';
import '../../css/game-selectors.css';
import '../../css/game-maps.css';

export default function CleptocraciaPage() {
  const [step, setStep] = useState('selectAvatar');
  const [playerAvatar, setPlayerAvatar] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadMessage, setLoadMessage] = useState('');
  const [governors, setGovernors] = useState({});
  const [prefeitos, setPrefeitos] = useState({});
  const [cityDetail, setCityDetail] = useState(null);
  const [selectedVereador, setSelectedVereador] = useState(null);

  useEffect(() => {
    axios.get('/politicos/governadores')
      .then((res) => { if (res.data?.success) setGovernors(res.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedState?.sigla) return;
    axios.get(`/politicos/prefeitos/${selectedState.sigla}`)
      .then((res) => { if (res.data?.success) setPrefeitos(res.data.data); })
      .catch(() => {});
  }, [selectedState?.sigla]);

  const handleAvatarSelect = (avatar) => {
    setPlayerAvatar(avatar);
    setStep('auth');
  };

  const handleAuthContinue = () => {
    setStep('selectState');
  };

  const handleLoadGame = useCallback(async (saveInfo) => {
    const saveData = await useAuthStore.getState().loadGame(saveInfo.slot);
    if (!saveData?.gameState) return;

    const gs = saveData.gameState;
    const av = saveData.avatarData;
    const ver = saveData.vereadorData;

    if (av) setPlayerAvatar(av);
    if (ver) setSelectedVereador(ver);
    if (gs.cityData) {
      setSelectedCity({ code: gs.cityData.codigoIbge, name: gs.cityData.cityName });
      setSelectedState({ sigla: gs.cityData.stateSigla, name: gs.cityData.stateName });
    }

    // Restore full store state
    useGameStore.setState({
      ...gs,
      phase: gs.phase || 'playing',
    });

    // Fetch cityDetail for sidebar data
    if (gs.cityData?.codigoIbge) {
      try {
        const detailRes = await axios.get(`/municipio/${gs.cityData.codigoIbge}`);
        if (detailRes.data?.success) setCityDetail(detailRes.data.data);
      } catch {}
    }

    setStep('playing');
  }, []);

  const handleSelectState = (sigla, name) => {
    setSelectedState({ sigla, name });
    setStep('selectCity');
  };

  const handleSelectCity = async (code, name, tradeData) => {
    setSelectedCity({ code, name, ...tradeData });
    setCityDetail(null);

    setLoadProgress(0);
    setLoadMessage('Carregando dados do municipio...');
    setStep('loadingGame');

    let products = { exports: [], imports: [] };
    let detail = null;
    const progressTimer = setInterval(() => {
      setLoadProgress((p) => Math.min(p + 2, 35));
    }, 80);

    let newsScenarios = [];
    try {
      const [prodRes, detailRes, scenariosRes] = await Promise.all([
        axios.get(`/comex/municipality-products/${GAME_YEAR}/${selectedState.sigla}/${code}`).catch(() => null),
        axios.get(`/municipio/${code}`).catch(() => null),
        axios.get('/game/scenarios?limit=12').catch(() => null),
      ]);
      if (prodRes?.data?.success) products = prodRes.data.data;
      if (detailRes?.data?.success) detail = detailRes.data.data;
      if (scenariosRes?.data?.success) newsScenarios = scenariosRes.data.data || [];
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
    }
    clearInterval(progressTimer);

    setCityDetail(detail);
    useGameStore.getState().setNewsDecisions(newsScenarios);
    setLoadProgress(40);
    setLoadMessage(newsScenarios.length > 0
      ? `${newsScenarios.length} cenarios reais carregados...`
      : 'Preparando cenario economico...');

    await new Promise((r) => setTimeout(r, 300));
    useGameStore.getState().reset();
    const eleitos = detail?.politicos?.eleitos || [];
    const candidatos = detail?.politicos?.candidatos || [];
    const vereadores = eleitos.filter(e => e.cargo === 'VEREADOR');
    const partidos = [...new Set(candidatos.map(c => c.partido).filter(Boolean))];
    const partidoVereadores = [...new Set(vereadores.map(v => v.partido).filter(Boolean))];

    useGameStore.setState({
      cityData: {
        cityName: name,
        codigoIbge: code,
        stateSigla: selectedState.sigla,
        stateName: selectedState.name,
        populacao: detail?.populacao || 0,
        exportValue: tradeData.export || 0,
        importValue: tradeData.import || 0,
        balance: tradeData.balance || 0,
        topExports: (products.exports || []).map((p) => p.productName || p.product).filter(Boolean),
        topImports: (products.imports || []).map((p) => p.productName || p.product).filter(Boolean),
        fotos: detail?.fotos || [],
        fotosHistoricas: detail?.fotosHistoricas || [],
        totalVereadores: vereadores.length,
        totalCandidatos: candidatos.length,
        partidos,
        partidoVereadores,
        prefeito: eleitos.find(e => e.cargo === 'PREFEITO'),
        pib: detail?.pib || null,
      },
    });
    setLoadProgress(60);
    setLoadMessage('Inicializando o jogo...');

    await new Promise((r) => setTimeout(r, 200));
    setLoadProgress(90);
    setLoadMessage('Entrando na prefeitura...');

    await new Promise((r) => setTimeout(r, 400));
    setLoadProgress(100);

    await new Promise((r) => setTimeout(r, 300));
    setStep('cityIntro');
  };

  const handleRestart = () => {
    useGameStore.getState().reset();
    setStep('selectState');
    setSelectedState(null);
    setSelectedCity(null);
    setCityDetail(null);
    setSelectedVereador(null);
    setPrefeitos({});
  };


  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-0">
      <SEO
        title="Cleptocracia - Jogo de Política Municipal"
        description="Assuma o governo de uma cidade brasileira e enfrente decisões reais de política municipal neste jogo de simulação."
        path="/cleptocracia"
      />
      {/* Header */}
      {step !== 'playing' && step !== 'briefing' && step !== 'meetVereador' && (
        <div className="mb-3 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <Gamepad2 size={24} className="text-[var(--accent)] sm:w-8 sm:h-8" />
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text)]">Cleptocracia</h1>
          </div>
          <p className="text-xs sm:text-sm text-[var(--muted)]">
            {step === 'auth'
              ? 'Entre ou crie uma conta para salvar seu progresso'
              : step === 'chooseVereador'
              ? 'Escolha um vereador para trabalhar como assessor'
              : step === 'briefing'
              ? 'Revise seus atributos iniciais antes de começar'
              : step === 'cityIntro'
              ? `Conheca ${selectedCity?.name || 'a cidade'} antes de comecar`
              : step === 'loadingGame'
              ? 'Carregando dados do municipio...'
              : 'Comece como assessor, ganhe influencia e candidate-se na proxima eleicao'}
          </p>
        </div>
      )}

      {/* Step: Select Avatar */}
      {step === 'selectAvatar' && (
        <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] p-3 sm:p-6 min-h-[calc(100vh-120px)] sm:min-h-[600px] flex items-center justify-center">
          <AvatarSelector onSelect={handleAvatarSelect} />
        </div>
      )}

      {/* Step: Auth (Login/Register) */}
      {step === 'auth' && (
        <AuthScreen
          playerAvatar={playerAvatar}
          onContinue={handleAuthContinue}
          onLoadGame={handleLoadGame}
        />
      )}

      {/* Step: Select State */}
      {step === 'selectState' && (
        <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] p-3 sm:p-6 min-h-[calc(100vh-120px)] sm:min-h-[600px] flex items-center justify-center">
          <GameStateMap onSelect={handleSelectState} governors={governors} playerAvatar={playerAvatar} />
        </div>
      )}

      {/* Step: Select City */}
      {step === 'selectCity' && selectedState && (
        <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] p-3 sm:p-6 min-h-[calc(100vh-120px)] sm:min-h-[600px] flex items-center justify-center">
          <GameCityMap
            stateSigla={selectedState.sigla}
            stateName={selectedState.name}
            onSelect={handleSelectCity}
            onBack={() => setStep('selectState')}
            prefeitos={prefeitos}
            playerAvatar={playerAvatar}
          />
        </div>
      )}

      {/* Step: Loading Game */}
      {step === 'loadingGame' && (
        <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] overflow-hidden min-h-[calc(100vh-120px)] sm:min-h-[600px] flex flex-col">
          {cityDetail?.fotos?.[0] && (
            <div className="relative h-32 sm:h-40 overflow-hidden">
              <img src={cityDetail.fotos[0]} alt={selectedCity?.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1a1a2e]" />
            </div>
          )}

          <div className={`flex-1 flex flex-col items-center px-4 sm:px-6 pb-4 sm:pb-6 ${cityDetail?.fotos?.[0] ? '-mt-10 sm:-mt-12 relative z-10' : 'pt-4 sm:pt-6'}`}>
            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5 w-full max-w-2xl">
              {playerAvatar && (
                <div className="w-10 h-10 sm:w-14 sm:h-14 shrink-0 rounded-xl overflow-hidden border-2 border-white/10" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
              )}
              <div className="flex-1 min-w-0">
                {playerAvatar?.alias && (
                  <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mb-0.5"
                    style={{ color: 'rgba(212,165,116,.8)' }}>{playerAvatar.alias}</p>
                )}
                <h2 className="text-xl sm:text-2xl font-bold text-white truncate">
                  {selectedCity?.name}
                  {selectedState && <span className="text-gray-400 font-normal">, {selectedState.sigla}</span>}
                </h2>
                <div className="flex items-center gap-2 sm:gap-3 mt-0.5 flex-wrap">
                  {cityDetail?.gentilico && <span className="text-[10px] sm:text-xs text-gray-400 italic">{cityDetail.gentilico}</span>}
                  {cityDetail?.populacao > 0 && (
                    <span className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1">
                      <Users size={9} /> {formatNum(cityDetail.populacao)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="w-full max-w-2xl mb-5">
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#e94560] to-[#ff6b81]"
                  style={{ width: `${loadProgress}%`, transition: 'width 0.3s ease-out' }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[11px] text-gray-400">{loadMessage}</span>
                <span className="text-[11px] text-gray-500">{loadProgress}%</span>
              </div>
            </div>

            <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-3">
              {cityDetail?.politicos?.eleitos?.length > 0 && (() => {
                const prefeito = cityDetail.politicos.eleitos.find(e => e.cargo === 'PREFEITO') || cityDetail.politicos.eleitos[0];
                return (
                  <div className="p-3.5 rounded-lg bg-white/[.04] border border-white/[.08]">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Crown size={10} className="text-amber-400" /> Prefeito(a) eleito(a)
                    </p>
                    <div className="flex items-center gap-3">
                      <CandidatoAvatar
                        candidato={prefeito}
                        foto={prefeito.foto}
                        nome={prefeito.nome}
                        size={44}
                        className="rounded-lg border border-white/10"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{capitalize(prefeito.nome)}</p>
                        <p className="text-xs text-gray-400">{prefeito.partido} - {capitalize(prefeito.ocupacao || '')}</p>
                        <p className="text-[10px] text-gray-500">{capitalize(prefeito.grauInstrucao || '')}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {(cityDetail?.pib || cityDetail?.comercio) && (
                <div className="p-3.5 rounded-lg bg-white/[.04] border border-white/[.08] md:col-span-1">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Landmark size={10} className="text-blue-400" /> Economia
                  </p>
                  {cityDetail?.pib && (
                    <>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <p className="text-[10px] text-gray-400">PIB ({cityDetail.pib.ano})</p>
                          <p className="text-sm font-bold text-white">{formatBRL(cityDetail.pib.pibTotal * 1000)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400">Per capita</p>
                          <p className="text-sm font-bold text-white">{formatBRL(cityDetail.pib.pibPerCapita)}</p>
                        </div>
                      </div>
                      {(() => {
                        const vab = cityDetail.pib.vabTotal || 1;
                        const sectors = [
                          { label: 'Servicos', val: cityDetail.pib.vabServicos, color: '#60a5fa', icon: Building2 },
                          { label: 'Industria', val: cityDetail.pib.vabIndustria, color: '#f59e0b', icon: Factory },
                          { label: 'Agro', val: cityDetail.pib.vabAgro, color: '#34d399', icon: Wheat },
                          { label: 'Adm. Pub.', val: cityDetail.pib.vabAdmPublica, color: '#a78bfa', icon: Landmark },
                        ];
                        return (
                          <div className="space-y-1 mb-2">
                            {sectors.map(s => {
                              const pct = vab > 0 ? ((s.val / vab) * 100) : 0;
                              if (pct < 0.5) return null;
                              return (
                                <div key={s.label} className="flex items-center gap-2">
                                  <s.icon size={9} style={{ color: s.color }} className="shrink-0" />
                                  <span className="text-[9px] text-gray-500 w-14 truncate">{s.label}</span>
                                  <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color, transition: 'width 0.5s' }} />
                                  </div>
                                  <span className="text-[9px] text-gray-500 w-8 text-right">{pct.toFixed(0)}%</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </>
                  )}
                  {cityDetail?.comercio && (cityDetail.comercio.exportacao > 0 || cityDetail.comercio.importacao > 0) && (
                    <div className={cityDetail?.pib ? 'pt-2 border-t border-white/5' : ''}>
                      <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                        <Globe size={8} className="text-emerald-400" /> Comercio exterior
                      </p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-[9px] text-gray-400">Exp.</p>
                          <p className="text-xs font-semibold text-green-400">{formatUSD(cityDetail.comercio.exportacao)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-400">Imp.</p>
                          <p className="text-xs font-semibold text-orange-400">{formatUSD(cityDetail.comercio.importacao)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-400">Saldo</p>
                          <p className={`text-xs font-semibold ${cityDetail.comercio.saldo >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatUSD(cityDetail.comercio.saldo)}
                          </p>
                        </div>
                      </div>
                      {cityDetail.comercio.topExportacoes?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {cityDetail.comercio.topExportacoes.slice(0, 3).map((p, i) => (
                            <span key={i} className="text-[8px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-300 truncate max-w-[120px]">
                              {capitalize(p.produto)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {cityDetail?.politicos && (
                <div className="p-3.5 rounded-lg bg-white/[.04] border border-white/[.08]">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Users size={10} className="text-purple-400" /> Eleicoes 2024
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[10px] text-gray-400">Candidatos</p>
                      <p className="text-sm font-bold text-white">{cityDetail.politicos.total}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Eleitos</p>
                      <p className="text-sm font-bold text-green-400">{cityDetail.politicos.eleitos.length}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400">Partidos</p>
                      <p className="text-sm font-bold text-blue-400">
                        {new Set(cityDetail.politicos.candidatos.map(c => c.partido)).size}
                      </p>
                    </div>
                  </div>
                  {cityDetail.politicos.eleitos.length > 1 && (
                    <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
                      {cityDetail.politicos.eleitos.filter(e => e.cargo !== 'PREFEITO').slice(0, 4).map((e, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CandidatoAvatar candidato={e} foto={e.foto} nome={e.nome} size={20} className="rounded-full" />
                          <span className="text-[10px] text-gray-300 truncate flex-1">{capitalize(e.nome)}</span>
                          <span className="text-[9px] text-gray-500">{e.partido}</span>
                        </div>
                      ))}
                      {cityDetail.politicos.eleitos.filter(e => e.cargo !== 'PREFEITO').length > 4 && (
                        <p className="text-[9px] text-gray-500 text-center">
                          +{cityDetail.politicos.eleitos.filter(e => e.cargo !== 'PREFEITO').length - 4} eleitos
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step: City Intro */}
      {step === 'cityIntro' && cityDetail && (
        <CityIntroScreen
          cityDetail={cityDetail}
          selectedCity={selectedCity}
          selectedState={selectedState}
          playerAvatar={playerAvatar}
          onContinue={() => setStep('chooseVereador')}
        />
      )}

      {/* Step: Choose Vereador */}
      {step === 'chooseVereador' && cityDetail && (
        <VereadorSelector
          cityDetail={cityDetail}
          selectedCity={selectedCity}
          playerAvatar={playerAvatar}
          onSelect={(ver) => {
            setSelectedVereador(ver);
            setStep('meetVereador');
          }}
          onBack={() => setStep('cityIntro')}
        />
      )}

      {/* Step: Meet Vereador — intro scene */}
      {step === 'meetVereador' && playerAvatar && selectedVereador && (
        <MeetVereadorScreen
          vereador={selectedVereador}
          selectedCity={selectedCity}
          playerAvatar={playerAvatar}
          onContinue={() => setStep('briefing')}
          onBack={() => { setSelectedVereador(null); setStep('chooseVereador'); }}
        />
      )}

      {/* Step: Briefing do Gabinete */}
      {step === 'briefing' && playerAvatar && selectedVereador && (() => {
        const base = {
          carisma: playerAvatar.stats[0] || 0,
          influencia: playerAvatar.stats[1] || 0,
          recursos: playerAvatar.stats[2] || 0,
          risco: playerAvatar.stats[3] || 0,
        };
        const ind = selectedVereador?.indicadores || {};
        const bonusCalc = (v) => Math.max(1, Math.round((v || 0) / 10));
        const bonus = {
          carisma: bonusCalc(ind.carisma),
          influencia: bonusCalc(ind.influencia),
          recursos: bonusCalc(ind.recursos),
          risco: bonusCalc(ind.risco),
        };
        const total = {
          carisma: base.carisma + bonus.carisma,
          influencia: base.influencia + bonus.influencia,
          recursos: base.recursos + bonus.recursos,
          risco: base.risco + bonus.risco,
        };
        const totalStats = [total.carisma, total.influencia, total.recursos, total.risco];
        const maxStat = Math.max(...totalStats, 1);
        const radarStats = totalStats.map(v => (v / maxStat) * 100);

        const statRows = [
          { label: 'Carisma', icon: <Sparkles size={14} />, baseVal: base.carisma, bonusVal: bonus.carisma, totalVal: total.carisma, color: 'text-green-400', bg: 'bg-green-500/10', barColor: 'bg-green-400' },
          { label: 'Influência', icon: <Shield size={14} />, baseVal: base.influencia, bonusVal: bonus.influencia, totalVal: total.influencia, color: 'text-purple-400', bg: 'bg-purple-500/10', barColor: 'bg-purple-400' },
          { label: 'Recursos', icon: <DollarSign size={14} />, baseVal: base.recursos, bonusVal: bonus.recursos, totalVal: total.recursos, color: 'text-amber-400', bg: 'bg-amber-500/10', barColor: 'bg-amber-400' },
          { label: 'Risco', icon: <AlertTriangle size={14} />, baseVal: base.risco, bonusVal: bonus.risco, totalVal: total.risco, color: 'text-red-400', bg: 'bg-red-500/10', barColor: 'bg-red-400' },
        ];

        const handleStartGame = () => {
          const allVereadores = cityDetail?.politicos?.eleitos?.filter(e => e.cargo === 'VEREADOR') || [];
          const totalVereadores = allVereadores.length || 13;
          const currentCityData = useGameStore.getState().cityData || {};
          const fullCityData = {
            ...currentCityData,
            vereador: selectedVereador ? capitalize(selectedVereador.nome) : null,
            vereadorPartido: selectedVereador?.partido,
            vereadorBonus: bonus,
            avatarBase: base,
            vereadorVotos: selectedVereador?.votos || 0,
            vereadorSqCandidato: selectedVereador?.sqCandidato || null,
            vereadorPosition: selectedVereador?.position || totalVereadores,
            totalVereadores,
            partyVereadores: allVereadores.filter(e => e.partido === selectedVereador?.partido)?.length || 1,
            gameVereadores: allVereadores,
          };
          useGameStore.getState().startGame(fullCityData, base, bonus);
          setStep('playing');
        };

        return (
          <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] overflow-hidden min-h-[calc(100vh-80px)] sm:min-h-[600px] flex flex-col">
            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-white/[.06]">
              <div className="flex items-center gap-2 sm:gap-3 mb-1">
                <Briefcase size={20} className="text-[var(--accent)] sm:w-6 sm:h-6" />
                <h2 className="text-lg sm:text-xl font-bold text-white">Briefing do Gabinete</h2>
              </div>
              <p className="text-xs sm:text-sm text-gray-400">
                Atributos iniciais em {selectedCity?.name}
              </p>
            </div>

            <div className="flex-1 px-4 sm:px-6 py-4 sm:py-6 flex flex-col lg:flex-row gap-4 sm:gap-6 items-start overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              <div className="lg:w-64 shrink-0 space-y-3 sm:space-y-4 w-full lg:w-64">
                {/* Player */}
                <div className="p-4 rounded-xl bg-white/[.03] border border-white/[.08]">
                  <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Seu Personagem</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden border border-white/10" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
                    <div>
                      <p className="text-sm font-bold text-white">{playerAvatar.alias}</p>
                      <div className="flex gap-1 mt-1">
                        {STAT_LABELS.map((l, i) => (
                          <span key={l} className="text-[8px] px-1 py-0.5 rounded bg-white/[.06] text-gray-400">{l}: {playerAvatar.stats[i]}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vereador */}
                <div className="p-4 rounded-xl bg-white/[.03] border border-white/[.08]">
                  <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-2">Seu Chefe</p>
                  <div className="flex items-center gap-3">
                    <CandidatoAvatar candidato={selectedVereador} foto={selectedVereador.foto} nome={selectedVereador.nome} size={48} className="rounded-lg border border-white/10" />
                    <div>
                      <p className="text-sm font-bold text-white">{capitalize(selectedVereador.nome)}</p>
                      <p className="text-xs text-gray-400">{selectedVereador.partido} · Vereador(a)</p>
                    </div>
                  </div>
                </div>

                {/* Radar */}
                <div className="flex justify-center">
                  <RadarChart stats={radarStats} size={180} color="#d4a574" />
                </div>
              </div>

              {/* Right: stat calculation table */}
              <div className="flex-1 min-w-0">
                <div className="rounded-xl border border-white/[.08] overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-4 gap-0 text-center px-4 py-2.5 bg-white/[.03] border-b border-white/[.06]">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider text-left pl-1">Atributo</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Base</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Bônus</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Total</div>
                  </div>

                  {/* Rows */}
                  {statRows.map((s, i) => {
                    const isDanger = s.label === 'Risco';
                    return (
                      <div key={s.label} className={`grid grid-cols-4 gap-0 items-center px-4 py-3 ${isDanger ? 'bg-red-500/[.03]' : ''} ${i < statRows.length - 1 ? 'border-b border-white/[.04]' : ''}`}>
                        <div className="flex items-center gap-2 text-left">
                          <span className={s.color}>{s.icon}</span>
                          <span className={`text-xs font-semibold ${isDanger ? 'text-red-300' : 'text-gray-300'}`}>{s.label}</span>
                          {isDanger && <span className="text-[7px] text-red-400/60 uppercase tracking-wider">perigo</span>}
                        </div>
                        <div className="text-center">
                          <span className={`text-sm font-mono ${isDanger ? 'text-red-400/60' : 'text-gray-400'}`}>{s.baseVal}</span>
                        </div>
                        <div className="text-center">
                          <span className={`text-sm font-mono font-bold ${isDanger ? 'text-red-400' : s.color}`}>
                            {isDanger ? (s.bonusVal > 0 ? `⚠ +${s.bonusVal}` : '—') : `+${s.bonusVal}`}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className={`inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 rounded-md text-sm font-bold ${s.bg} ${s.color}`}>
                            {s.totalVal}{isDanger ? '%' : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Legend */}
                  <div className="px-4 py-3 bg-white/[.02] border-t border-white/[.06]">
                    <div className="flex flex-wrap gap-4 text-[10px] text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />
                        Base = habilidades do perfil "{playerAvatar.alias}"
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-[var(--accent)] inline-block" />
                        Bônus = assessorar {capitalize(selectedVereador.nome)} ({selectedVereador.partido})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Info callout */}
                <div className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                  <p className="text-[11px] text-amber-300/80 leading-relaxed">
                    <Briefcase size={11} className="inline mr-1 -mt-0.5" />
                    Como assessor de <strong>{capitalize(selectedVereador.nome)}</strong>, seus atributos iniciais
                    refletem tanto seu perfil quanto a influência do gabinete.
                  </p>
                </div>
                <div className="mt-2 p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                  <p className="text-[11px] text-red-300/80 leading-relaxed">
                    <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />
                    <strong>Risco é perigoso:</strong> quanto maior, mais chances de escândalos e investigações.
                    Acima de 80% a Lei da Ficha Limpa impede candidaturas. Se atingir 100%, fim de jogo.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-white/[.06] flex items-center justify-between gap-2">
              <button onClick={() => { setSelectedVereador(null); setStep('chooseVereador'); }}
                className="flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-medium border border-white/10 text-gray-300 hover:text-white hover:border-white/20 transition-colors shrink-0">
                <ArrowLeft size={12} className="sm:hidden" />
                <span className="hidden sm:inline">Trocar Vereador</span>
                <span className="sm:hidden">Voltar</span>
              </button>
              <button
                onClick={handleStartGame}
                className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all flex-1 sm:flex-none justify-center"
                style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}>
                Entrar no Gabinete <ChevronRight size={16} />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Step: Playing */}
      {step === 'playing' && (
        <GameBoard
          playerAvatar={playerAvatar}
          selectedCity={selectedCity}
          selectedState={selectedState}
          cityDetail={cityDetail}
          selectedVereador={selectedVereador}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
